import {useCallback, useEffect, useRef, useState} from 'react'

import type {TopicItem} from '../utils/copilot-chat-types'
// eslint-disable-next-line no-restricted-imports
import {useChatState} from '../utils/CopilotChatContext'
import {useChatManager} from '../utils/CopilotChatManagerContext'

/**
 * Fetches repository items from a custom endpoint.
 *
 * Note: `_searchUserReposFirst` is unused because the backend now automatically
 * detects user login prefix in queries and prioritizes those repos.
 */
export function useRepositoryItems(
  filterText: string,
  open: boolean,
  topicDisplayCount?: number,
  ownerDisplayLogin?: string,
  includeForks: boolean = false,
  isActive: boolean = true,
) {
  const {topRepositoriesCache, currentRepository} = useChatState()
  const chatManager = useChatManager()
  const {service} = chatManager

  const [repositories, setRepositories] = useState<TopicItem[]>([])
  const [loading, setLoading] = useState<true | false | 'initial'>(!topRepositoriesCache ? 'initial' : false)

  // Track if we've fetched top repos
  const hasFetchedTopReposRef = useRef(false)

  // Reset cache so fresh data is fetched on next open
  const resetTopRepoResults = useCallback(() => {
    chatManager.setTopRepositoryTopics(undefined)
    hasFetchedTopReposRef.current = false
  }, [chatManager])

  // Currently only used by Spaces to filter repositories by the owner for org-owned spaces.
  const isOrgOwned = ownerDisplayLogin && ownerDisplayLogin.trim() !== ''

  // Fetch top repositories when opened (initial load)
  useEffect(() => {
    if (!isActive) return

    const fetchTopRepos = async () => {
      if (!open || hasFetchedTopReposRef.current || topRepositoriesCache) return

      setLoading('initial')
      try {
        // Call with no query to get top repositories
        // Backend handles prioritization and sorting
        const topRepos = await service.fetchRepositoriesSearch(
          undefined,
          topicDisplayCount,
          includeForks,
          currentRepository?.id,
        )

        chatManager.setTopRepositoryTopics(topRepos)
        hasFetchedTopReposRef.current = true
      } catch {
        // Silently catch errors - return empty results gracefully
      } finally {
        setLoading(false)
      }
    }

    if (!isOrgOwned) {
      void fetchTopRepos()
    }
  }, [
    isActive,
    open,
    service,
    isOrgOwned,
    topicDisplayCount,
    currentRepository,
    chatManager,
    topRepositoriesCache,
    includeForks,
  ])

  // Search repositories when filter text changes
  useEffect(() => {
    if (!isActive) return

    const abortController = new AbortController()

    const fetchSearchedRepos = async (query: string) => {
      setLoading(true)

      try {
        // Backend handles all search logic: owner extraction, user login detection, top repo prioritization
        // For org-owned spaces, pass the owner to filter by org
        const results = await service.fetchRepositoriesSearch(
          query,
          undefined, // limit
          includeForks,
          currentRepository?.id,
          isOrgOwned ? ownerDisplayLogin : undefined,
        )

        if (!abortController.signal.aborted) {
          setRepositories(results)
        }
      } catch {
        // Silently catch errors - return empty results gracefully
        if (!abortController.signal.aborted) {
          setRepositories([])
        }
      } finally {
        if (!abortController.signal.aborted) {
          setLoading(false)
        }
      }
    }

    if (filterText || isOrgOwned) {
      void fetchSearchedRepos(filterText)
    } else if (topRepositoriesCache) {
      // eslint-disable-next-line react-hooks/set-state-in-effect
      setRepositories(topRepositoriesCache)
    }

    return () => {
      abortController.abort()
    }
  }, [
    isActive,
    filterText,
    isOrgOwned,
    ownerDisplayLogin,
    currentRepository,
    service,
    topRepositoriesCache,
    includeForks,
  ])

  // Refresh list of top repositories based on the selected owner
  useEffect(() => {
    if (!isActive) return
    chatManager.setTopRepositoryTopics(undefined)
    hasFetchedTopReposRef.current = false
  }, [isActive, chatManager, ownerDisplayLogin])

  // If we have cached top repos and no filter, return them
  if (topRepositoriesCache && !filterText && !isOrgOwned) {
    return {repositories: topRepositoriesCache, loading: false, resetTopRepoResults}
  }

  return {repositories, loading, resetTopRepoResults}
}
